  class TabbableContents extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      if (!this.tablist) return;
      
      this.tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      
      if (this.tabs.length === 0 || this.panels.length === 0) return;
      
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      
      this.tablist.addEventListener('click', this.onClick);
      this.tablist.addEventListener('keydown', this.onKeydown);
      this.syncInitialState();
    }
    
    disconnectedCallback() {
      if (!this.tablist) return;
      this.tablist.removeEventListener('click', this.onClick);
      this.tablist.removeEventListener('keydown', this.onKeydown);
    }
    
    syncInitialState() {
      let selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      if (!selectedTab) {
        selectedTab = this.tabs[0];
      }
      this.activateTab(selectedTab, false, true);
    }
    
    activateTab(nextTab, moveFocus = true, force = false) {
      if (!nextTab) return;
      if (!force && nextTab.getAttribute('aria-selected') === 'true') return;
      
      const targetId = nextTab.getAttribute('data-tab-target');
      if (!targetId) return;
      
      let activePanel = null;
      
      for (const tab of this.tabs) {
        const selected = tab === nextTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      }
      
      for (const panel of this.panels) {
        const isActivePanel = panel.id === targetId;
        panel.hidden = !isActivePanel;
        if (isActivePanel) activePanel = panel;
      }
      
      this.refreshPanelSliders(activePanel);
      
      if (moveFocus) {
        nextTab.focus({ preventScroll: true });
      }
    }
    
    refreshPanelSliders(panel) {
      if (!panel) return;
      
      const sliders = panel.querySelectorAll('.collection__slider');
      if (sliders.length === 0) return;
      
      const refresh = () => {
        sliders.forEach((slider) => {
          if (window.Flickity && typeof window.Flickity.data === 'function') {
            const instance = window.Flickity.data(slider);
            if (instance) {
              instance.resize();
              instance.reposition();
              return;
            }
          }
          
          slider.dispatchEvent(new Event('resize'));
        });
        
        window.dispatchEvent(new Event('resize'));
      };
      
      requestAnimationFrame(() => requestAnimationFrame(refresh));
    }
    
    handleClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !this.tablist.contains(tab)) return;
      this.activateTab(tab, false);
    }
    
    handleKeydown(event) {
      const currentIndex = this.tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;
      
      let nextIndex = null;
      
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
        case 'ArrowLeft':
        case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
        case 'Home':
        nextIndex = 0;
        break;
        case 'End':
        nextIndex = this.tabs.length - 1;
        break;
        default:
        return;
      }
      
      event.preventDefault();
      this.activateTab(this.tabs[nextIndex]);
    }
  }
  
  if (!customElements.get('tabbable-contents')) {
    customElements.define('tabbable-contents', TabbableContents);
  }